import { differenceInDays, parseISO } from 'date-fns';
import { head, intersection, last, uniq } from 'lodash';

import { ENTIRE_AVAILABLE, MODE_AIR, TRANSFER_TRANSFER } from '@/lib/constants';
import { sumTotalCo2Emissions } from '@/lib/models/emissions/utils/count-emissions';
import {
  RouteBlockAttributesInterface,
  RouteScheduleInterface,
  RouteScheduleSegmentInterface,
  RouteScheduleSequenceInterface,
  RouteVehicleCoverage,
  ScheduleRouteResultInterface,
} from '@/types';
import {
  RouteCarriersApiV2Interface,
  RouteLocationsApiV2Interface,
  RouteVehiclesApiV2Interface,
  ScheduleRouteApiInterface,
  ScheduleRouteSegmentApiInterface,
  ScheduleRouteSegmentSequenceApiInterface,
  SchedulesResultApiInterface,
  SegmentModeType,
} from '@/types/api-types';
import { getCarrierIdsFromScheduleSegments } from '@/utils/carrier';
import { convertModeVerb, formatRouteLocation, formatSegmentDescription } from '@/utils/format';
import { convertTransferType, generateId, generateSegmentKey, getPreferredIdentifier } from '@/utils/helpers';
import { formatFlightNumber } from '@/utils/strings';
import {
  determineVehicleCoverage,
  getVehicleDelay,
  getVehicleFilterAttributes,
  getVehicleHauling,
  getVehicleIdsFromScheduleSegment,
  getVehicleName,
} from '@/utils/vehicle';

import { extractSegments, getRouteViaLocations, getSegmentStopCount, getSegmentTransferCount } from './utils';

export const transformScheduleResults = (result: SchedulesResultApiInterface): ScheduleRouteResultInterface => {
  const { carriers, locations, vehicles, lines } = result;
  const routes = result.routes.map((route) => convertRoute(route, locations, carriers, vehicles));
  // Return response object
  return { carriers, locations, routes, vehicles, lines };
};

const convertRoute = (
  route: ScheduleRouteApiInterface,
  locations: RouteLocationsApiV2Interface,
  carriers: RouteCarriersApiV2Interface,
  vehicles: RouteVehiclesApiV2Interface,
): RouteScheduleInterface => {
  const { durationSeconds: routeDuration } = route;

  // Sequences
  const firstSequence = head(route.sequences);
  const lastSequence = last(route.sequences);

  // We assume that for a route to exist it _must_ have at least one sequence
  if (!firstSequence || !lastSequence) throw new Error('Missing route sequences');

  // Extra Segments from Sequences (flatten structure out)
  const routeSegments = extractSegments(route);
  const firstSegment = head(routeSegments);
  const lastSegment = last(routeSegments);

  // We assume that for a route to exist it _must_ have at least one segment
  if (!firstSegment || !lastSegment) throw new Error('Missing route segments');

  const { departureDateTime, proformaDepartureDateTime } = firstSequence;
  const { arrivalDateTime, proformaArrivalDateTime } = lastSequence;

  const { fromId, type } = firstSegment;
  const { toId = '' } = lastSegment;

  const fromLocation = formatRouteLocation(fromId, locations);
  const toLocation = formatRouteLocation(toId, locations);

  const segments = type ? processSegments(routeSegments, locations, carriers, vehicles, type, routeDuration) : [];
  const sequences = processSequences(route.sequences);
  const isPredicted = segments.some((segment) => segment.isPredicted);
  const emissions = segments.map((segment) => segment.emissions);

  const lineIds = segments.reduce((accum, s) => {
    return [...accum, ...(s.lineIds || [])];
  }, [] as string[]);

  const uniqLineIds = uniq(lineIds);

  const forecast = buildScheduleForecast({
    departureDateTime,
    arrivalDateTime,
    proformaDepartureDateTime,
    proformaArrivalDateTime,
  });

  return {
    id: generateId(),
    mode: type,
    duration: routeDuration,
    from: fromLocation!,
    to: toLocation!,
    segments,
    sequences,
    arrivalDateTime: forecast.forecastArrivalDateTime,
    departureDateTime: forecast.forecastDepartureDateTime,
    forecast,
    attributes: getRouteAttributes(routeSegments, type, vehicles),
    isPredicted,
    emissions: sumTotalCo2Emissions(emissions),
    lineIds: uniqLineIds,
    via: getRouteViaLocations(routeSegments),
  };
};

const processSegments = (
  segments: ScheduleRouteSegmentApiInterface[],
  locations: RouteLocationsApiV2Interface,
  carriers: RouteCarriersApiV2Interface,
  vehicles: RouteVehiclesApiV2Interface,
  routeType: SegmentModeType,
  routeDuration: number,
): RouteScheduleSegmentInterface[] => {
  return segments.map((segment) => {
    const {
      arrivalDateTime,
      departureDateTime,
      fromId,
      toId,
      carrierId,
      carrierIds,
      lineIds,
      durationSeconds: duration,
      type,
      vehicleId,
      transferType,
      stops,
      vehicleNumber,
      fromTerminal,
      toTerminal,
      predicted,
      emissions,
    } = segment;

    const segmentTransferType = transferType ? convertTransferType(transferType, routeType) : 'unknown';
    const modePrefix = convertModeVerb(type);
    const fromLocation = formatRouteLocation(fromId, locations);
    const toLocation = toId ? formatRouteLocation(toId, locations) : null;
    const segmentKey = generateSegmentKey(fromLocation, toLocation);

    // Get Carrier and Flight details
    const carrier = carriers[carrierId];
    const carrierIdentifier = getPreferredIdentifier(carrier.identifiers)?.value;

    // Get vehicle
    const vehicle = vehicles[vehicleId];
    const vehicleName = vehicle ? getVehicleName(vehicle) : '';
    const vehicleHauling = vehicle ? getVehicleHauling(vehicle) : '';
    const vehicleDelay = getVehicleDelay(vehicle, departureDateTime);

    // Generate description
    const description = formatSegmentDescription(
      fromLocation,
      toLocation,
      modePrefix,
      routeType,
      type,
      segmentTransferType,
    );

    const scheduleSegment: RouteScheduleSegmentInterface = {
      id: generateId(),
      key: segmentKey,
      description,
      arrivalDateTime: parseISO(arrivalDateTime),
      departureDateTime: parseISO(departureDateTime),
      from: fromLocation!,
      to: toLocation!,
      fromTerminal: fromTerminal || '',
      toTerminal: toTerminal || '',
      duration,
      mode: type,
      stops: stops || [],
      transferType: segmentTransferType,
      carrierId,
      carrierIds,
      lineIds,
      vehicleId,
      percent: (duration / routeDuration) * 100,
      attributes: {
        flightNumber: formatFlightNumber(carrierIdentifier, vehicleNumber),
        vehicleName,
        vehicleHauling,
        vehicleDelay,
      },
      emissions,
      isPredicted: predicted,
    };

    return scheduleSegment;
  });
};

export const processSequences = (
  sequences: ScheduleRouteSegmentSequenceApiInterface[],
): RouteScheduleSequenceInterface[] => {
  return sequences.reduce((accum, sequence) => {
    // skip transfers
    if (sequence.type === TRANSFER_TRANSFER) return accum;

    const forecast = buildScheduleForecast({
      departureDateTime: sequence.departureDateTime,
      arrivalDateTime: sequence.arrivalDateTime,
      proformaDepartureDateTime: sequence.proformaDepartureDateTime,
      proformaArrivalDateTime: sequence.proformaArrivalDateTime,
    });

    const scheduleSequence: RouteScheduleSequenceInterface = {
      forecast,
      durationSeconds: sequence.durationSeconds,
      toId: sequence.toId,
      fromId: sequence.fromId,
      type: sequence.type,
    };

    return [...accum, scheduleSequence];
  }, [] as RouteScheduleSequenceInterface[]);
};

const buildScheduleForecast = ({
  departureDateTime,
  arrivalDateTime,
  proformaDepartureDateTime,
  proformaArrivalDateTime,
}: {
  departureDateTime: string;
  arrivalDateTime: string;
  proformaDepartureDateTime?: string;
  proformaArrivalDateTime?: string;
}) => {
  const arrivalDateTimeAsDate = parseISO(arrivalDateTime);
  const departureDateTimeAsDate = parseISO(departureDateTime);
  const proformaArrivalDateTimeAsDate = proformaArrivalDateTime
    ? parseISO(proformaArrivalDateTime)
    : arrivalDateTimeAsDate;
  const proformaDepartureDateTimeAsDate = proformaDepartureDateTime
    ? parseISO(proformaDepartureDateTime)
    : departureDateTimeAsDate;

  return {
    departureDelta: differenceInDays(departureDateTimeAsDate, proformaDepartureDateTimeAsDate),
    arrivalDelta: differenceInDays(arrivalDateTimeAsDate, proformaArrivalDateTimeAsDate),
    scheduledArrivalDateTime: proformaArrivalDateTimeAsDate,
    scheduledDepartureDateTime: proformaDepartureDateTimeAsDate,
    forecastArrivalDateTime: arrivalDateTimeAsDate,
    forecastDepartureDateTime: departureDateTimeAsDate,
  };
};

export const getRouteAttributes = (
  segments: ScheduleRouteSegmentApiInterface[],
  mode: SegmentModeType,
  vehicles: RouteVehiclesApiV2Interface,
): RouteBlockAttributesInterface => {
  // Get aircraft/vehicle attributes
  const vehicleIds = getVehicleIdsFromScheduleSegment(segments);
  const { hasFreightOnlyVehicles, widebody, narrowbody, hasRfs } = getVehicleAttributes(mode, vehicleIds, vehicles);

  return {
    allCarriers: getCarrierIdsFromScheduleSegments(segments),
    primaryCarriers: getPrimaryCarriers(segments),
    hasFreightOnlyVehicles,
    aircraft: widebody && narrowbody ? { widebody, narrowbody } : null,
    transferCount: getSegmentTransferCount(segments),
    stopCount: getSegmentStopCount(segments),
    hasRfs: hasRfs || false,
  };
};

const getVehicleAttributes = (
  mode: SegmentModeType,
  vehicleIds: string[],
  vehicles: RouteVehiclesApiV2Interface,
): {
  hasFreightOnlyVehicles: RouteVehicleCoverage;
  widebody?: RouteVehicleCoverage;
  narrowbody?: RouteVehicleCoverage;
  hasRfs?: boolean;
} => {
  if (mode === MODE_AIR) {
    const attributes = vehicleIds.map((id): any => {
      const vehicle = vehicles[id];
      return vehicle ? getVehicleFilterAttributes(vehicle) : null;
    });
    return determineVehicleCoverage(attributes);
  }
  return {
    hasFreightOnlyVehicles: ENTIRE_AVAILABLE,
    hasRfs: false,
  };
};

const getPrimaryCarriers = (segments: ScheduleRouteSegmentApiInterface[]): string[] => {
  const filteredSegments = segments.filter((segment) => segment.type !== TRANSFER_TRANSFER);
  const allIds = filteredSegments.map((segment) => segment.carrierIds || [segment.carrierId]);
  const intersectingCarriers = intersection(...allIds);
  return uniq(intersectingCarriers);
};
